import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { addMinutes } from 'date-fns';
import { createTimeslot, getJob } from '../services/api';
import { Job } from '../models/types';

const CreateTimeslotPage: React.FC = () => {
  const navigate = useNavigate();
  const { jobId } = useParams<{ jobId: string }>();
  
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
    const [formData, setFormData] = useState({
    startTime: new Date(),
    endTime: addMinutes(new Date(), 60),
    requiredVolunteers: 1,
    goodieThreshold: 60, // Default 60 minutes
    description: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  useEffect(() => {
    const fetchJob = async () => {
      if (!jobId) return;
      
      try {
        setLoading(true);
        const jobData = await getJob(jobId);
        setJob(jobData);
        
        const startTime = new Date();
        startTime.setMinutes(0, 0, 0);
        const endTime = addMinutes(startTime, 60);
          setFormData(prevFormData => ({
          ...prevFormData,
          startTime,
          endTime,
        }));
      } catch (err) {
        console.error('Error fetching job details:', err);        setLoadError('Job-Details konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
      } finally {
        setLoading(false);
      }
    };

    fetchJob();
  }, [jobId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const parsedValue = parseInt(value, 10);
    if (!isNaN(parsedValue) && parsedValue > 0) {
      setFormData({ ...formData, [name]: parsedValue });
      if (errors[name]) {
        setErrors({ ...errors, [name]: '' });
      }
    }
  };

  const handleStartTimeChange = (dateTime: Date | null) => {
    if (dateTime) {
      setFormData({ 
        ...formData, 
        startTime: dateTime,
        endTime: addMinutes(dateTime, 60)  
      });
      
      if (errors.startTime) {
        setErrors({ ...errors, startTime: '' });
      }
    }
  };

  const handleEndTimeChange = (dateTime: Date | null) => {
    if (dateTime) {
      setFormData({ ...formData, endTime: dateTime });
      
      if (errors.endTime) {
        setErrors({ ...errors, endTime: '' });
      }
    }
  };
  const validate = () => {
    const newErrors: Record<string, string> = {};
      if (formData.startTime >= formData.endTime) {
      newErrors.endTime = 'Die Endzeit muss nach der Startzeit liegen';
    }
    
    if (formData.requiredVolunteers <= 0) {
      newErrors.requiredVolunteers = 'Die Anzahl der benötigten Helfer muss größer als 0 sein';
    }
    
  
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!jobId) return;
    if (!validate()) return;
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      await createTimeslot({
        jobId,
        startTime: formData.startTime.toISOString(),
        endTime: formData.endTime.toISOString(),
        requiredVolunteers: formData.requiredVolunteers,
        goodieThreshold: formData.goodieThreshold,
        description: formData.description,
      });
      
      navigate(`/admin/jobs/${jobId}`);
    } catch (err) {
      console.error('Failed to create timeslot:', err);
      setSubmitError('Zeitfenster konnte nicht erstellt werden. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Lade Job-Details...
        </Typography>
      </Container>
    );
  }

  if (loadError) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{loadError}</Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>          <Button variant="contained" onClick={() => navigate(`/admin/jobs/${jobId}`)}>
            Zurück zu Job-Details
          </Button>
        </Box>
      </Container>
    );
  }    if (!job) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">Job nicht gefunden</Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button variant="contained" onClick={() => navigate('/admin')}>
            Zurück zum Admin-Dashboard
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>          Zeitfenster hinzufügen für {job.name}
        </Typography>
        
        <Paper sx={{ p: 3 }}>
          {submitError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {submitError}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <DateTimePicker
                    label="Startzeit"
                    value={formData.startTime}
                    onChange={handleStartTimeChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!errors.startTime,
                        helperText: errors.startTime,
                      }
                    }}
                  />
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <DateTimePicker
                    label="Endzeit"
                    value={formData.endTime}
                    onChange={handleEndTimeChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!errors.endTime,
                        helperText: errors.endTime,
                      }
                    }}
                  />
                </Box>
              </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    required
                    fullWidth
                    id="requiredVolunteers"
                    label="Benötigte Helfer"
                    name="requiredVolunteers"
                    type="number"
                    InputProps={{ inputProps: { min: 1 } }}
                    value={formData.requiredVolunteers}
                    onChange={handleNumberChange}
                    error={!!errors.requiredVolunteers}
                    helperText={errors.requiredVolunteers}
                  />
                </Box>
                
               
              </Stack>
              
              <Box>
                <TextField
                  fullWidth
                  id="description"
                  label="Beschreibung (Optional)"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                />
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/admin/jobs/${jobId}`)}
                  disabled={isSubmitting}
                >
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Erstelle...' : 'Zeitfenster erstellen'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default CreateTimeslotPage;

